import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { namespace } from '@common-config/constants';
import { backendApi, type MyBridgeAgentCalendarTest } from '@common/reducers/backend-api/backend-api';
import { CalendarCheck, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';

/**
 * The agent's own Google Calendar, reached through a Google service account the
 * owner pastes (backend agent-calendar → the agent's `calendar` MCP tool). The JSON
 * key is WRITE-ONLY: it is sent once and never shown again — the backend returns
 * only the calendar id, time zone and the service account's email, which the owner
 * must share the calendar with. Saved on its own (not with the agent form): it is
 * a credential, not an agent setting.
 *
 * LEGACY: shown only for an agent already connected this way — Google Calendar is otherwise
 * connected from the agent's Integrations (sign in with Google).
 */
export interface GoogleCalendarSectionProps {
  agentId: string;
  classes: { label: string; hint: string; input: string; textarea: string };
}

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * A backend reason code (agent-calendar: test verdicts + validation errors) →
 * `myBridge.calendar.reasons.<code>`; the backend's English text is the fallback
 * for a code this UI does not know (or no code at all).
 */
export const localizeCalendarReason = (
  t: (key: string, options: { email: string; defaultValue: string }) => string,
  reason: string | undefined,
  fallback: string,
  email?: string,
): string => (reason ? t(`myBridge.calendar.reasons.${reason}`, { email: email ?? '', defaultValue: fallback }) : fallback);

/** A backend error body: `{ message, data: { reason } }` (agent-calendar's stable reason codes). */
const errorBody = (e: unknown): { message?: string; reason?: string } => {
  const data = (e as { data?: { message?: string; data?: { reason?: string } } })?.data;
  return { message: data?.message, reason: data?.data?.reason };
};

export const GoogleCalendarSection: React.FC<GoogleCalendarSectionProps> = ({ agentId, classes }) => {
  const { t } = useTranslation(namespace);
  const { confirm, dialog } = useConfirmDialog();
  const { data } = backendApi.useGetMyBridgeAgentCalendarQuery({ agentId });
  const [save, { isLoading: saving }] = backendApi.useSetMyBridgeAgentCalendarMutation();
  const [disconnect, { isLoading: disconnecting }] = backendApi.useDeleteMyBridgeAgentCalendarMutation();
  const [runTest, { isLoading: testing }] = backendApi.useTestMyBridgeAgentCalendarMutation();

  const connected = data?.connected === true ? data : null;
  const [calendarId, setCalendarId] = useState('');
  const [timeZone, setTimeZone] = useState(browserTimeZone);
  const [keyJson, setKeyJson] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<MyBridgeAgentCalendarTest | null>(null);

  useEffect(() => {
    if (connected) {
      setCalendarId(connected.calendarId);
      setTimeZone(connected.timeZone);
    }
  }, [connected?.calendarId, connected?.timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Localize a backend reason code (`myBridge.calendar.reasons.<code>`); the
   * backend's English text is the fallback for a code this UI does not know.
   */
  const reasonText = (reason: string | undefined, fallback: string): string =>
    localizeCalendarReason(t, reason, fallback, connected?.serviceAccountEmail);
  const errorText = (e: unknown, fallbackKey: string): string => {
    const { message, reason } = errorBody(e);
    return reasonText(reason, message || t(fallbackKey));
  };

  const onSave = async () => {
    setError(null);
    setVerdict(null);
    try {
      await save({ agentId, calendarId: calendarId.trim(), timeZone: timeZone.trim(), ...(keyJson.trim() ? { serviceAccountJson: keyJson } : {}) }).unwrap();
      setKeyJson('');
    } catch (e) {
      setError(errorText(e, 'myBridge.calendar.saveFailed'));
    }
  };

  const onTest = async () => {
    setError(null);
    try {
      setVerdict(await runTest({ agentId }).unwrap());
    } catch (e) {
      setError(errorText(e, 'myBridge.calendar.testFailed'));
    }
  };

  const onDisconnect = async () => {
    if (!(await confirm({ content: t('myBridge.calendar.disconnectConfirm'), title: t('confirmDialog.title'), confirmLabel: t('confirmDialog.disconnect') }))) return;
    setError(null);
    setVerdict(null);
    try {
      await disconnect({ agentId }).unwrap();
      setCalendarId('');
      setKeyJson('');
    } catch (e) {
      setError(errorText(e, 'myBridge.calendar.saveFailed'));
    }
  };

  const canSave = calendarId.trim() !== '' && timeZone.trim() !== '' && (connected !== null || keyJson.trim() !== '');

  return (
    <div className="space-y-3" data-testid="agent-google-calendar">
      {dialog}
      <p className={classes.hint}>{t('myBridge.calendar.hint')}</p>

      {connected && (
        <div className="space-y-1 rounded-ai-md bg-ai-muted px-3 py-2" data-testid="agent-calendar-connected">
          <div className="flex items-center gap-1.5 text-sm font-medium text-ai-text">
            <CalendarCheck className="h-4 w-4" />
            <span dir="ltr">{t('myBridge.calendar.connectedAs', { email: connected.serviceAccountEmail })}</span>
          </div>
          <p className={classes.hint}>{t('myBridge.calendar.shareHint', { email: connected.serviceAccountEmail })}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="agent-calendar-id" className={classes.label}>{t('myBridge.calendar.calendarId')}</Label>
          <Input
            id="agent-calendar-id"
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            placeholder={t('myBridge.calendar.calendarIdPlaceholder')}
            className={classes.input}
            dir="ltr"
            data-testid="agent-calendar-id"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="agent-calendar-tz" className={classes.label}>{t('myBridge.calendar.timeZone')}</Label>
          <Input
            id="agent-calendar-tz"
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            placeholder={t('myBridge.googleCalendar.timeZonePlaceholder', 'Asia/Jerusalem')}
            className={classes.input}
            dir="ltr"
            data-testid="agent-calendar-tz"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="agent-calendar-key" className={classes.label}>{t('myBridge.calendar.keyLabel')}</Label>
        <Textarea
          id="agent-calendar-key"
          value={keyJson}
          onChange={(e) => setKeyJson(e.target.value)}
          placeholder={t('myBridge.calendar.keyPlaceholder')}
          rows={4}
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          className={`font-mono text-xs ${classes.textarea}`}
          data-testid="agent-calendar-key"
        />
        {connected && <p className={classes.hint}>{t('myBridge.calendar.keyReplaceHint')}</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={onSave} disabled={!canSave || saving} data-testid="agent-calendar-save">
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          <span className={saving ? 'ms-1.5' : ''}>{t('myBridge.calendar.save')}</span>
        </Button>
        {connected && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={onTest} disabled={testing} data-testid="agent-calendar-test">
              {testing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span className={testing ? 'ms-1.5' : ''}>{t('myBridge.calendar.test')}</span>
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onDisconnect} disabled={disconnecting} data-testid="agent-calendar-disconnect">
              {t('myBridge.calendar.disconnect')}
            </Button>
          </>
        )}
      </div>

      {verdict?.ok === true && <p className="text-sm text-green-600" data-testid="agent-calendar-verdict">{t('myBridge.calendar.testOk')}</p>}
      {verdict?.ok === false && <p className="text-sm text-red-500" data-testid="agent-calendar-verdict">{reasonText(verdict.reason, verdict.hint)}</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};
